import { useCallback, useContext, useState } from 'react';
import { noop, stubTrue } from 'lodash-es';

import {
  useCreateAddressBookEntry,
  useEditAddressBookEntry,
  useRemoveAddressBookEntry,
} from '../../app/graphql/addressBookActions';
import { useTemplatesActiveItem } from '../../components/popups/templates/templatePopupUtils';
import {
  formToInputAddressBookEntry,
  graphQLToFormAddressBookEntry,
} from '../../app/data/addressBookConversions';
import { DialogsParentContext } from '../../components/dialogs/DialogsParentContext';
import { useDialogControls } from '../../common/utils/dialogUtils';

export function useAddressBookLogic(accountNumber, setData, onClose) {
  const { activeItem, setActiveItem, afterSave } = useTemplatesActiveItem({
    mapItemToForm: graphQLToFormAddressBookEntry,
    isEqual: stubTrue,
  });

  const createAddressBookEntry = useCreateAddressBookEntry({ accountNumber });
  const editAddressBookEntry = useEditAddressBookEntry({ accountNumber });
  const removeAddressBookEntry = useRemoveAddressBookEntry({ accountNumber });

  const onCancel = useCallback(() => setData(), [setData]);

  const [searchValue, searchOnChange] = useState();

  const { setDialogOpen } = useContext(DialogsParentContext);
  const noDataDialog = useDialogControls({ onOpenChange: setDialogOpen });

  const openNoDataDialog =
    noDataDialog.isOpen || !accountNumber
      ? noop
      : () => {
          noDataDialog.open();
          onClose?.();
        };

  const REMOVE_PROPS = {
    onDelete: async id => {
      await removeAddressBookEntry(id);
      onCancel();
    },
    removeTextId: 'addressBook.delete.dialog.text',
    removeSuccessMessageId: 'addressBook.delete.success',
  };

  const onSave = async (id, formValues) => {
    const input = formToInputAddressBookEntry(formValues, accountNumber);
    const response = id
      ? await editAddressBookEntry(id, input)
      : await createAddressBookEntry(input);
    onCancel();
    afterSave({ ...input, id: response.id });
  };

  const getEditInitialValues = entry => graphQLToFormAddressBookEntry(entry);

  return {
    activeItem,
    setActiveItem,
    afterSave,
    onCancel,
    onSave,
    REMOVE_PROPS,
    searchValue,
    searchOnChange,
    noDataDialog,
    openNoDataDialog,
    getEditInitialValues,
  };
}
